import apiClient from "./apiClient";
import { updatePatientPathology } from "./PatientPathologyService";
import { getPathPabReportByPatient, updateReportStatus } from "./pathLabTestReportService";

const API_URL = "/labtests/book";
const getPatientBookingsUrl = (patientId) => `/labtests/book/patient/${patientId}`;

const asBookingList = (payload, depth = 0) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object" || depth > 3) return [];

  const nestedKeys = [
    "data",
    "bookings",
    "labBookings",
    "labTests",
    "content",
    "items",
    "records",
    "result",
    "results",
    "booking",
  ];
  for (const key of nestedKeys) {
    if (!(key in payload)) continue;
    const nested = asBookingList(payload[key], depth + 1);
    if (nested.length) return nested;
  }

  if (
    payload.labTestId != null ||
    payload.lab_test_id != null ||
    payload.prescriptionId != null ||
    payload.prescription_id != null ||
    payload.id != null
  ) {
    return [payload];
  }
  return [];
};

export const createPatientLabBooking = async (payload) => {
  const response = await apiClient.post(API_URL, payload);
  return response.data;
};

const asReportList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const CLOSED_REPORT_STATUSES = new Set(["COMPLETE", "COMPLETED", "BILLED", "COLLECTED"]);

/** Booked pending tests become PENDING so path desk can bill, collect, and add a result. */
const syncBookedLabTestStatus = async ({ patientId, prescriptionId, labTestId }) => {
  try {
    await updatePatientPathology(patientId, prescriptionId, labTestId, "PENDING", null);
  } catch (error) {
    console.warn("[patientLabBooking] Pathology status was not set to PENDING:", error);
  }

  try {
    const reports = asReportList(await getPathPabReportByPatient(patientId));
    const matches = reports.filter((report) => {
      const reportTestId = report?.testId ?? report?.labTestId ?? report?.lab_test_id;
      if (reportTestId == null || String(reportTestId) !== String(labTestId)) return false;
      const reportPrescriptionId = report?.prescriptionId ?? report?.prescription_id;
      if (
        prescriptionId != null &&
        reportPrescriptionId != null &&
        String(reportPrescriptionId) !== String(prescriptionId)
      ) {
        return false;
      }
      const status = String(
        report?.resultStatus ?? report?.result_status ?? report?.status ?? ""
      ).toUpperCase();
      return !CLOSED_REPORT_STATUSES.has(status);
    });

    await Promise.all(
      matches.map(async (report) => {
        const reportId = report?.reportId ?? report?.id;
        if (reportId == null) return;
        const status = String(
          report?.resultStatus ?? report?.result_status ?? report?.status ?? ""
        ).toUpperCase();
        if (status === "PENDING") return;
        await updateReportStatus(reportId, "PENDING", report?.result ?? null);
      })
    );
  } catch (error) {
    console.warn("[patientLabBooking] Report status was not set to PENDING:", error);
  }
};

export const bookPrescribedLabTests = async ({
  patientId,
  prescriptionId,
  labTestIds,
  preferredDate,
  preferredTime,
  notes,
}) => {
  const ids = [
    ...new Set(
      (Array.isArray(labTestIds) ? labTestIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    ),
  ];
  const timeNote = preferredTime ? `Preferred time: ${preferredTime}` : "";
  const bookingNotes = [notes, timeNote].filter(Boolean).join(". ");
  const bookings = await Promise.all(
    ids.map((labTestId) =>
      createPatientLabBooking({
        patientId,
        prescriptionId,
        labTestId,
        preferredDate,
        notes:
          bookingNotes ||
          `Patient portal booking against prescription #${prescriptionId}`,
      })
    )
  );
  await Promise.all(
    ids.map((labTestId) =>
      syncBookedLabTestStatus({ patientId, prescriptionId, labTestId })
    )
  );
  invalidatePatientLabBookingCache(patientId);
  return bookings;
};

export const getPatientLabBookings = async (patientId) => {
  if (patientId == null || patientId === "") return [];
  const response = await apiClient.get(getPatientBookingsUrl(patientId));
  return asBookingList(response?.data);
};

const bookingCache = new Map();
const BOOKING_CACHE_MS = 2 * 60 * 1000;

export const invalidatePatientLabBookingCache = (patientId) => {
  if (patientId == null || patientId === "") {
    bookingCache.clear();
    return;
  }
  bookingCache.delete(Number(patientId));
};

/** Patient booking records. GET /labtests/book is not a list route (the server reads "book" as an id). */
export const listLabBookingsForPatients = async (patientIds = []) => {
  const ids = [
    ...new Set(
      (Array.isArray(patientIds) ? patientIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    ),
  ];
  const now = Date.now();
  const collected = [];
  const missing = [];
  ids.forEach((id) => {
    const cached = bookingCache.get(id);
    if (cached && now - cached.at < BOOKING_CACHE_MS) {
      collected.push(...cached.bookings);
      return;
    }
    missing.push(id);
  });

  for (let index = 0; index < missing.length; index += 12) {
    const batch = missing.slice(index, index + 12);
    const lists = await Promise.all(
      batch.map(async (id) => {
        try {
          const bookings = await getPatientLabBookings(id);
          bookingCache.set(id, { at: Date.now(), bookings });
          return bookings;
        } catch {
          return [];
        }
      })
    );
    lists.forEach((bookings) => collected.push(...bookings));
  }
  return collected;
};
